import { Input, OdinIcon, OdinIconSize } from '@myosh/odin-components';
import classNames from 'classnames';
import { t } from 'i18next';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLayoutAdmin } from '../../hooks/useIsLayoutAdmin';
import type { Layout } from '../../redux/config/layout';
import { useAppDispatch } from '../../redux/hooks';
import { newTabStateActions } from '../../redux/slices/new-tab-slice';
import { useNewTabId } from '../Charts/hooks/useNewTabId';
import { DeleteTabIcon } from './delete-tab-icon';
import { TabHeaderSettingsPopover } from './tab-header-settings-popover';

export type DeleteTabHeaderProps = TabHeaderSettingsProps & {
  onlyIcon: boolean;
  title: string;
};
type TabHeaderSettingsProps = {
  updateLayout: (newValues: Partial<Layout>, removeLayout: boolean) => void;
};
type HeaderElementProps = TabHeaderSettingsProps & {
  setTabInEditMode: (edit: boolean) => void;
  isEditable?: boolean;
  layoutId: number | string;
  layout: Layout;
  localIndex: number;
};

export const TabViewHeaderElement = ({
  setTabInEditMode,
  isEditable = false,
  updateLayout,
  layoutId,
  layout,
  localIndex,
}: HeaderElementProps) => {
  const [inputVisible, setInputVisible] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string | undefined>(layout.title);
  const editIconId = `edit-icon-${layoutId}`;
  const isAdminLayout = useLayoutAdmin();
  const [title, setTitle] = useState<string>(layout.title);
  const dispatch = useAppDispatch();

  const updateLayoutOnEscape = (event: { key?: string; type: string }) => {
    if (closeCallback(event) && inputValue && inputValue.trim() !== title) {
      updateLayout({ title: inputValue }, false);
    }
  };

  const closeCallback = useMemo(() => {
    return (event: { key?: string; type: string }): boolean => {
      if ((event.key && (event.key === 'Enter' || event.key === 'Escape')) || event.type === 'blur') {
        if (inputVisible) {
          setInputVisible(false);
          setTabInEditMode(false);
        }
        return true;
      }
      return false;
    };
  }, [inputVisible, setTabInEditMode]);

  const newTabId = useNewTabId();

  useEffect(() => {
    if (isEditable && newTabId && newTabId === layoutId) {
      if (!inputVisible) {
        handlePencilClick();
      }
      dispatch(newTabStateActions.setNewTabId(undefined));
    }
  }, [newTabId, isEditable, layoutId, dispatch]);

  useEffect(() => {
    setTitle(layout.title);
    setInputValue(layout.title);
  }, [layout]);

  const titleClassName = classNames('tabTitle text-sm whitespace-nowrap', {
    truncate: isEditable,
    'noDrop h-full justify-center': !isEditable,
    editableTab: isEditable,
    userLayout: layout.isUserLayout,
    publicLayout: !layout.isUserLayout,
  });

  useLayoutEffect(() => {
    if (titleRef.current) {
      titleRef.current?.closest('li')?.setAttribute('data-position', String(localIndex));
    }
  }, [localIndex]);
  const titleRef = useRef<HTMLDivElement>(null);
  const titleElement = useMemo(() => {
    return (
      <div className={titleClassName} data-id={layoutId} id={`headerTab-${layoutId}`} ref={titleRef} title={title}>
        {title}
      </div>
    );
  }, [title, layoutId, titleClassName]);

  const settingsDelete = useMemo(() => {
    const deleteTabElement = <DeleteTabIcon title={title} updateLayout={updateLayout} onlyIcon={!isAdminLayout} />;
    if (isAdminLayout && layout) {
      return <TabHeaderSettingsPopover layout={layout}> {deleteTabElement}</TabHeaderSettingsPopover>;
    }
    return deleteTabElement;
  }, [isAdminLayout, title, layout, updateLayout]);

  const handlePencilClick = () => {
    if (inputVisible) return; // already in edit mode, do nothing
    setInputVisible(true);
    setTabInEditMode(true);
  };

  return (
    <div className="flex h-full flex-row items-center">
      <span
        title={layout.isUserLayout ? t('privateDropOnly') : t('publicDropOnly')}
        className={classNames('flex max-w-min mr-1', { invisible: !isEditable })}
      >
        <OdinIcon icon="GripVertical" size={OdinIconSize.ExtraSmall} />
      </span>
      {isEditable && inputVisible ? (
        <div
          className="w-48"
          onBlur={updateLayoutOnEscape}
          onKeyUp={updateLayoutOnEscape}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Input
            placeholder={title}
            name={title}
            value={inputValue}
            inputStyles="m-1 rounded-md whitespace-nowrap flex items-center p-1 justify-center"
            inputRef={(el) => {
              if (el) {
                (el as HTMLInputElement).focus();
              }
            }}
            onChangeEvent={(event) => {
              setInputValue(event.target.value);
            }}
          />
        </div>
      ) : (
        titleElement
      )}
      <div
        className={classNames('flex w-fit flex-row gap-1 p-1 pl-2 overflow-hidden', { invisible: !isEditable })}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          id={editIconId}
          data-id={layoutId}
          data-pr-at={title}
          onClick={() => {
            if (!isEditable) return;
            setInputVisible(!inputVisible);
            setTabInEditMode(!inputVisible);
          }}
        >
          <OdinIcon icon="Pencil" size={OdinIconSize.ExtraSmall} />
        </div>
        {settingsDelete}
      </div>
    </div>
  );
};
